/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import React, { useEffect, useRef, useState } from "react"
import useResizeObserver from "@react-hook/resize-observer"
import { Menu } from "../Components/Menu"
import { brandColors, externalColors } from "../Components/EOSThemeProvider"
import { icons } from "../constants/icons"
import { Grid, IconButton } from "@cd2/components"
import { DropdownMenu } from "../Components/DropdownMenu"
import { IDynamicMessageData, IEmail, use365Mail } from "../Components/Buttons/EmailWith365Button"
import { removeKeys } from "../utils/object"
import { useGlobalState } from "../Components/GlobalStateProvider"
import { useLockData } from "../hooks/locks"
import { useSiteAlerts } from "../hooks/useSiteAlerts"
import { P } from "@cd2/text"
import { useSettings } from "../utils/useSettings"
import { useHistory, useRouteMatch } from "react-router-dom"
import { CreateTaskForResourceButton } from "../Pages/TasksPage"
import { IComponentProp } from "@cd2/tools"
import { buttonConfigs } from "../constants/buttonConfigs"
import { OutlookLogo } from "../assets/svgComponents/OutlookLogo"
import { useEvents } from "./EventsList"
import { apiHost } from "../constants/apiHost"
import { joinUrl } from "../utils/utils"
import { toolbarTooltipKey } from "../utils/linkTooltips"

export function Toolbar({ items: toolbarItems }: { items: any[] }) {
  // the matched route pattern (e.g. /quotes/:id) lets a tooltip apply to a button on every record
  const path = useRouteMatch()?.path ?? ""
  const items = toolbarItems.filter(Boolean).map(item => {
    const tooltipKey = toolbarTooltipKey(path, item)
    return tooltipKey ? { ...item, tooltipKey } : item
  })
  const ref = useRef<HTMLDivElement>(null)
  const [amount, setAmount] = useState(0)
  const MORE_ITEM_WIDTH = 100

  useResizeObserver(ref, () => {
    setSliceAmount()
  })

  useEffect(() => {
    setSliceAmount()
  }, [])

  function setSliceAmount() {
    if (ref.current) {
      // measure the width of the toolbar
      const width = ref.current.getBoundingClientRect().width - MORE_ITEM_WIDTH

      // measure the width of the items with the classname menu-item and save the value of each in an array
      const itemWidths = Array.from(ref.current?.querySelectorAll(".menu-item")).map(
        (item: any) => item.getBoundingClientRect().width,
      )

      // calculate how many items can fit in the toolbar
      const itemsThatFit = itemWidths.reduce(
        (acc, itemWidth) => {
          if (acc.total + itemWidth < width) {
            acc.count++
            acc.total += itemWidth
          }
          return acc
        },
        { count: 0, total: 0 },
      )

      setAmount(itemsThatFit.count)
    }
  }

  const menuCss = css`
    padding: 0;
  `

  return (
    <div
      css={css`
        background-color: ${brandColors.midGrey};
        padding: 8px 16px;
        @media (max-width: 768px) {
          padding: 8px 16px;
        }
      `}
    >
      <div
        ref={ref}
        css={css`
          height: 0;
          overflow: hidden;
        `}
      >
        <Menu css={menuCss} items={items} toolbar />
      </div>
      <div
        css={css`
          display: flex;
          width: 100%;
        `}
      >
        <Menu css={menuCss} items={[...items.slice(0, amount)]} toolbar />
        <div
          css={css`
            flex: 1;
          `}
        />
        <div>
          {items.slice(amount).length > 0 && (
            <Menu
              css={menuCss}
              items={[
                {
                  icon: icons.ellipsis,
                  subMenuItems: items
                    .slice(amount)
                    .map(item => removeKeys(item, ["buttonColor", "color", "tooltip"])),
                  submenuAsDropdown: true,
                  children: "More",
                },
              ]}
              toolbar
            />
          )}
        </div>
      </div>
    </div>
  )
}

const primaryAction = {
  buttonColor: brandColors.night,
  color: "white",
  iconColor: brandColors.mintPop,
}
const dangerAction = (
  opts?: {
    confirmation?: { heading: string; body: React.ReactNode }
    disabled?: boolean
  } & Record<string, any>,
) => {
  const { confirmation, disabled } = opts || {}
  return {
    children: "Delete",
    buttonColor: disabled ? undefined : brandColors.red,
    color: disabled ? undefined : "white",
    iconColor: disabled ? undefined : "white",
    confirmation: disabled
      ? undefined
      : confirmation
      ? {
          body: <React.Fragment>{confirmation.body}</React.Fragment>,
          heading: confirmation.heading,
        }
      : undefined,
    disable: !!disabled,
    ...opts,
  }
}
const createLink = ({ to, children }: { to: string; children?: string }) => ({
  to,
  icon: icons.plus,
  children: children ?? "Create new",
  ...primaryAction,
})
const backLink = ({ to }: { to: string }) => ({
  to,
  icon: icons.chevronLeft,
  children: "Back",
})

const editLink = ({
  children,
  to,
  disabled,
  tooltip,
}: {
  children?: string
  to: string
  disabled?: boolean
  tooltip?: string
}) => ({
  to,
  icon: icons.edit,
  children: children ?? "Edit",
  disabled,
  tooltip,
  ...primaryAction,
})

const office365 = ({
  mail365,
  tooltip,
  disabled,
  emailContent,
  afterSuccessful,
  dynamicMessageData,
  onGetAdditionalData,
}: {
  mail365: ReturnType<typeof use365Mail>
  tooltip?: React.ReactNode
  disabled?: boolean
  emailContent: IEmail
  afterSuccessful?(): void
  dynamicMessageData?: IDynamicMessageData
  onGetAdditionalData?: () => Promise<{
    includeQuoteAttachments?: boolean
  } | null>
}) => {
  if (!mail365.userDetails) {
    return
  }
  return {
    tooltip,
    disabled: disabled || mail365.isSending,
    href: mail365.isSignedInWith365
      ? undefined
      : `/microsoft/auth/signin?userId=${mail365.userDetails.id}`,
    children:
      !mail365.userDetails || disabled ? (
        "Send to Outlook"
      ) : !mail365.isSignedInWith365 ? (
        "Sign in to Office365"
      ) : (
        <Grid columns={["max-content", "max-content"]}>
          <div>
            Send to Outlook <br />
            <span
              css={css`
                font-size: 12px;
                opacity: 0.7;
              `}
            >
              Signed in as {mail365.userDetails.microsoft_user_details.userPrincipalName}
            </span>
          </div>
          <div onClick={e => e.stopPropagation()}>
            <DropdownMenu items={[{ children: "Sign out", onClick: mail365.forceMsSignout }]}>
              <IconButton
                icon={{ icon: icons.settings, fixedWidth: true }}
                buttonStyle="text"
                padding={4}
                color="#666"
                square
              />
            </DropdownMenu>
          </div>
        </Grid>
      ),
    customIcon: (
      <OutlookLogo
        width="20px"
        css={css`
          width: 16px;
          position: relative;
          margin: -4px 0;
        `}
      />
    ),
    onClick: mail365.isSignedInWith365
      ? async () => {
          let additionalData = null
          if (onGetAdditionalData) {
            additionalData = await onGetAdditionalData()
          }
          mail365.handleSendMail({
            email: emailContent,
            afterSuccessful,
            dynamicMessageData,
            additionalData,
          })
        }
      : undefined,
    style: {
      padding: mail365.isSignedInWith365 && !disabled ? "4px 4px 4px 12px" : undefined,
    },
  }
}

const downloadPdf = ({
  to,
  children,
  tooltip,
  disabled,
  onClick,
}: {
  to?: string
  children?: string
  tooltip?: string
  disabled?: boolean
  onClick?: () => void
}) => ({
  href: to,
  children: children ?? "PDF",
  icon: icons.pdf,
  iconColor: externalColors.pdf,
  tooltip,
  disabled,
  onClick,
})
const downloadXls = ({
  to,
  children,
  queryParams,
  onClick,
}: {
  to: string
  children?: string
  queryParams?: Record<string, any>
  onClick?: (path: string) => void
}) => {
  const path = joinUrl(
    apiHost,
    [to, queryParams ? new URLSearchParams(JSON.parse(JSON.stringify(queryParams))) : null]
      .filter(Boolean)
      .join("?"),
  )
  return {
    // JSON string-parsing to remove undefined values
    href: onClick ? undefined : path,
    onClick: onClick ? () => onClick(path) : undefined,
    children: children ?? "XLS",
    icon: icons.xls,
    iconColor: externalColors.xls,
  }
}

const createTask = ({
  id,
  model,
}: {
  id: string
  model: IComponentProp<typeof CreateTaskForResourceButton, "model">
}) => ({
  render: () => (
    <div
      css={css`
        display: flex;
      `}
    >
      <CreateTaskForResourceButton recordId={id} model={model} />
    </div>
  ),
})

const archive = ({
  currentUser,
  model,
  afterArchive,
  id,
  isArchived,
  lockData,
  alerts,
}: {
  currentUser: ReturnType<typeof useGlobalState>["currentUser"]
  model:
    | "quotes"
    | "orders"
    | "deliveries"
    | "purchase_orders"
    | "orderRequests"
    | "jobs"
    | "stock_takes"
  afterArchive?: () => void
  id: string
  isArchived: boolean
  lockData: ReturnType<typeof useLockData>
  alerts: ReturnType<typeof useSiteAlerts>
}) => {
  const events = useEvents()
  const lock = lockData && lockData.find(lock => lock.resourceId === id)
  const disabled = Boolean(lock)

  async function handleArchive() {
    await window.skeem.mutate({ [model]: { update: { id, archived: !isArchived } } })
    afterArchive?.()
    alerts.genericInfo(isArchived ? "Record restored" : "Record archived")
    events.archivedRecord(model as any, id, { archived: !isArchived })
  }

  const canArchive =
    currentUser.isAdmin ||
    currentUser.isSuperAdmin ||
    (model === "orders" && currentUser.canArchiveOrders)

  if (!canArchive) {
    return null
  }

  if (isArchived) {
    return {
      children: "Restore",
      icon: icons.archive,
      onClick: handleArchive,
      iconColor: brandColors.orange,
      disabled,
    }
  }

  return {
    children: "Archive",
    icon: icons.archive,
    onClick: handleArchive,
    iconColor: brandColors.orange,
    disabled,
  }
}

const deleteButton = ({
  currentUser,
  model,
  redirectTo,
  id,
  lockData,
  alerts,
  settings,
  disabled,
  history,
  tooltip,
  beforeDelete,
}: {
  currentUser: ReturnType<typeof useGlobalState>["currentUser"]
  model: "quotes" | "orders" | "deliveries" | "purchase_orders" | "products"
  redirectTo: string
  id: string
  lockData: ReturnType<typeof useLockData>
  alerts: ReturnType<typeof useSiteAlerts>
  settings: ReturnType<typeof useSettings>
  history: ReturnType<typeof useHistory>
  disabled?: boolean
  tooltip?: string
  beforeDelete?: () => Promise<boolean> | boolean
}) => {
  let showDelete = false
  if ((settings.enableDelete && currentUser.isAdmin) || currentUser.isSuperAdmin) {
    showDelete = true
  }
  if (!showDelete) {
    return null
  }

  const lock = lockData && lockData.find(lock => lock.resourceId === id)
  const isDisabled = Boolean(lock) || disabled

  async function handleDelete() {
    try {
      if (beforeDelete) {
        const canDelete = await beforeDelete()
        if (!canDelete) {
          alerts.genericError("Unable to delete record")
          return
        }
      }
      await window.skeem.mutate({ [model]: { destroy: id } })
    } catch (e) {
      // a destroy is commonly rejected because other records still reference this one
      console.error(e)
      alerts.genericError(
        <P blockSpacing={0} color="white">
          <b>Unable to delete record</b> - {e instanceof Error ? e.message : String(e)}
        </P>,
      )
      return
    }
    history.push(redirectTo)
    alerts.genericError(
      <P blockSpacing={0} color="white">
        <b>Deleted</b> - Record deleted
      </P>,
    )
  }

  return {
    children: "Delete",
    icon: icons.delete,
    onClick: handleDelete,
    disabled: isDisabled,
    iconColor: brandColors.red,
    tooltip,
    confirmation: {
      body: (
        <React.Fragment>
          <P>
            Are you sure you want to delete this record?.
            <br />
            <b>This cannot be undone</b>
          </P>
        </React.Fragment>
      ),
      heading: "Delete record?",
    },
  }
}

function editArchivedButton({
  id,
  isArchived,
  lockData,
  to,
  tooltip,
  disabled,
}: {
  id: string
  isArchived: boolean
  lockData: ReturnType<typeof useLockData>
  to: string
  tooltip?: string | { isArchived?: string; locked?: string; edit?: string }
  disabled?: boolean
}) {
  const lock = lockData && lockData.find(lock => lock.resourceId === id)

  if (isArchived) {
    return {
      ...buttonConfigs.disabledDueToArchive,
      disabled: disabled ?? true,
      icon: icons.lock,
      tooltip: typeof tooltip === "string" ? tooltip : tooltip?.isArchived,
    }
  }

  if (lock) {
    return {
      ...buttonConfigs.beingEdited,
      icon: icons.lock,
      disabled: disabled ?? true,
      tooltip: typeof tooltip === "string" ? tooltip : tooltip?.locked,
    }
  }

  return Toolbar.editLink({
    to: to,
    disabled: disabled,
    tooltip: typeof tooltip === "string" ? tooltip : tooltip?.edit,
  })
}

Toolbar.primaryAction = primaryAction
Toolbar.dangerAction = dangerAction
Toolbar.createLink = createLink
Toolbar.editLink = editLink
Toolbar.office365 = office365
Toolbar.downloadPdf = downloadPdf
Toolbar.downloadXls = downloadXls
Toolbar.createTask = createTask
Toolbar.archive = archive
Toolbar.delete = deleteButton
Toolbar.backLink = backLink
Toolbar.editArchivedButton = editArchivedButton
